import { useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BookOpen, Boxes, ClipboardList, Coins, Gift, HeartHandshake, Image as ImageIcon, LayoutDashboard, LogOut, Mail, Package, Search, Settings as SettingsIcon,
  ShoppingCart, Store, Tag, Tags, Upload, Users, Layers, Workflow, LayoutTemplate, ScanText, DatabaseBackup, HelpCircle, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/import", label: "Import / Export", icon: Upload },
  { to: "/admin/cad-import", label: "CAD Import", icon: ScanText },
  { to: "/admin/backups", label: "Backups", icon: DatabaseBackup },
  { to: "/admin/mockups", label: "Mockups", icon: Sparkles },
  { to: "/admin/operations", label: "Operations", icon: ScanText },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/needs-pricing", label: "Needs Pricing", icon: ClipboardList },
  { to: "/admin/media", label: "Media Library", icon: ImageIcon },
  { to: "/admin/seo", label: "SEO / Redirects", icon: Search },
  { to: "/admin/guides", label: "Guides", icon: BookOpen },
  { to: "/admin/homepage", label: "Homepage", icon: LayoutTemplate },
  { to: "/admin/integrations", label: "Integrations", icon: Tags },
  { to: "/admin/collections", label: "Collections", icon: Layers },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes },
  { to: "/admin/rates", label: "Metal Rates", icon: Coins },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/crm", label: "CRM", icon: HeartHandshake, end: true },
  { to: "/admin/crm/journeys", label: "Journeys", icon: Workflow },
  { to: "/admin/discounts", label: "Discounts", icon: Tag },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
  { to: "/admin/email", label: "Auth & Email", icon: Mail },
  { to: "/admin/gift-cards", label: "Gift Cards", icon: Gift },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { config } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === null) navigate("/login?next=/admin");
    else if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-neutral-100 flex" data-testid="admin-layout">
      <aside className="hidden md:flex w-56 flex-col bg-neutral-950 text-neutral-400 fixed inset-y-0">
        <div className="px-5 h-16 flex items-center border-b border-neutral-800">
          <span className="font-heading font-bold text-white">
            {(config?.store_name || "AURA").toUpperCase()}<span className="text-brand">.</span>
            <span className="text-[10px] font-mono text-neutral-500 ml-2">ADMIN</span>
          </span>
        </div>
        <nav className="flex-1 py-4 px-2 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                  isActive ? "bg-neutral-800 text-white" : "hover:bg-neutral-900 hover:text-white"
                }`
              }
              data-testid={`admin-nav-${label.toLowerCase()}`}
            >
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-neutral-800 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-3 py-2 text-sm hover:text-white transition-colors" data-testid="admin-view-store">
            <Store size={17} /> View Store
          </Link>
          <button
            onClick={() => { logout(); navigate("/login"); }}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:text-white transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut size={17} /> Log Out
          </button>
        </div>
      </aside>
      <div className="flex-1 md:ml-56 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-neutral-200 flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex md:hidden gap-1 overflow-x-auto">
            {NAV.map(({ to, label, end }) => (
              <NavLink key={to} to={to} end={end} className={({ isActive }) => `px-2 py-1 text-xs whitespace-nowrap ${isActive ? "text-brand font-semibold" : "text-neutral-600"}`}>
                {label}
              </NavLink>
            ))}
          </div>
          <span className="hidden md:block text-sm text-neutral-500">
            Signed in as <span className="font-medium text-neutral-900">{user.email}</span>
          </span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-400" data-testid="admin-mode-badge">
            Razorpay: {config?.razorpay_mode || "…"} · Shiprocket: {config?.shiprocket_mode || "…"}
          </span>
        </header>
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
